@if (step() === 'setup') {
<div class="min-h-[calc(100vh-8rem)] w-full flex items-center justify-center overflow-visible backdrop-blur-sm px-4 pt-4">
    <div class="relative w-full max-w-xl">
        <!-- h-8 w-8: -top-4 -right-4 centers the circle on the corner (half in / half out) -->
        <button type="button"
            class="absolute -top-3 -right-3 z-30 h-8 w-8 rounded-full border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-center shadow-md hover:shadow-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
            (click)="goBack()" aria-label="Close create chatbot agent">
            <app-icon-v2 name="x" [size]="14" class="text-gray-600 dark:text-gray-300"></app-icon-v2>
        </button>

        <div
            class="rounded-2xl border border-gray-200 dark:border-gray-800 shadow-lg backdrop-blur-sm bg-white/90 dark:bg-gray-950/90 overflow-hidden">
        <div class="setup-wizard-track flex w-[200%] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
            [ngClass]="setupWizardStep() === 1 ? 'translate-x-0' : '-translate-x-1/2'">
            <!-- Step 1: product + agent name -->
            <div class="w-1/2 shrink-0 px-6 md:px-8 py-6 md:py-8 pt-12 flex flex-col justify-between gap-6">
                <div class="flex flex-col gap-0.5">
                    <p class="text-xs font-medium uppercase tracking-wide text-purple-600 dark:text-purple-400 mb-3">
                        Step 1 of 2
                    </p>
                    <h3 class="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                        Chatbot Setup - New Agent
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        Choose a product and a name that reflects your chatbot's purpose.
                    </p>
                </div>

                <div class="flex flex-col gap-2">
                    <div class="flex items-start justify-start gap-0.5">
                        <label for="productName" class="text-sm font-medium text-gray-700 dark:text-gray-100">
                            Product name
                        </label>
                        <app-icon-v2 name="asterisk" [size]="12" class="text-red-500 mt-0.5" />
                    </div>
                    <div class="relative w-full" appClickOutside (appClickOutside)="closeProductDropdown()">
                        <button type="button" (click)="toggleProductDropdown()" [disabled]="isSetupProductLocked()"
                            class="w-full flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition-all disabled:opacity-70 disabled:cursor-not-allowed
                               bg-white dark:bg-gray-900
                               border-gray-300 dark:border-gray-700
                               hover:border-purple-400 hover:shadow-md
                               focus:outline-none focus:ring-2 focus:ring-purple-500/60 focus:border-purple-500
                               dark:hover:border-line-strong dark:focus:border-purple-400
                               {{ productError() ? 'border-red-500 dark:border-red-400' : '' }}">
                            <span
                                class="truncate text-left {{ selectedProductId() ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500' }}">
                                @if (selectedProductId()) {
                                {{ selectedProductName() || 'Choose a product to configure the chatbot' }}
                                } @else {
                                Choose a product to configure the chatbot
                                }
                            </span>
                            <app-icon-v2 [name]="isSetupProductLocked() ? 'lock' : 'chevronDown'" [size]="16"
                                class="text-gray-500 dark:text-gray-400 ml-2 shrink-0" />
                        </button>

                        @if (isProductDropdownOpen() && !isSetupProductLocked()) {
                        <div
                            class="absolute left-0 right-0 mt-2 z-50 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl max-h-40 overflow-auto">
                            @if (productsList().length === 0) {
                            <div class="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                                No products available.
                            </div>
                            } @else {
                            @for (product of productsList(); track product.id) {
                            <button type="button"
                                class="w-full flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors
                                   hover:bg-purple-50 dark:hover:bg-white/10
                                   {{ selectedProductId() === product.id ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'text-gray-800 dark:text-gray-100' }}"
                                (click)="onProductChange(product.id)">
                                <span class="truncate">{{ product.name }}</span>
                                @if (selectedProductId() === product.id) {
                                <app-icon-v2 name="check" [size]="16"
                                    class="text-purple-600 dark:text-purple-400 ml-2 shrink-0" />
                                }
                            </button>
                            }
                            }
                        </div>
                        }
                    </div>
                    @if (productError()) {
                    <p class="text-sm text-red-500 dark:text-red-400 mx-2">
                        Please select a product to continue.
                    </p>
                    }
                </div>

                <div class="flex flex-col gap-2">
                    <div class="flex items-start justify-start gap-0.5">
                        <label for="agentName" class="text-sm font-medium text-gray-700 dark:text-gray-100">
                            Agent name
                        </label>
                        <app-icon-v2 name="asterisk" [size]="12" class="text-red-500 mt-0.5" />
                    </div>
                    <div class="relative w-full">
                        <input type="text" id="agentName" placeholder="Enter agent name..." maxlength="50"
                            [value]="agentName()" (input)="onAgentNameInput($event)" (keyup.enter)="onSetupWizardNext()"
                            class="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 pr-12 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow" />
                        <span
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-600 dark:text-gray-400 font-semibold">
                            {{ agentName().length }} / 50
                        </span>
                    </div>
                    @if (agentNameError()) {
                    <p class="text-sm text-red-500 dark:text-red-400 mx-2">
                        Please enter a valid agent name. <br> <span
                            class="text-xs text-gray-500 dark:text-gray-400">e.g.
                            Sales Assistant, Support Copilot, Research Analyst</span>
                    </p>
                    }
                </div>

                <div class="flex items-center justify-between pt-2">
                    <div class="flex items-center gap-1 border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-lg cursor-pointer"
                        (click)="onSetupWizardBack()">
                        <app-icon-v2 name="arrowLeft" [size]="14" class="text-gray-600 dark:text-gray-400" />
                        <span class="text-gray-600 dark:text-gray-400 font-semibold">Back</span>
                    </div>
                    <app-button variant="primary" className="flex items-center justify-center gap-2 text-sm font-medium"
                        (click)="onSetupWizardNext()">
                        Next
                        <app-icon-v2 name="arrowRight" [size]="14" class="inline-block" />
                    </app-button>
                </div>
            </div>

            <!-- Step 2: optional description (slides in from the right) -->
            <div class="w-1/2 shrink-0 px-6 md:px-8 py-6 md:py-8 pt-12 flex flex-col gap-6 min-h-[min(28rem,70vh)]">
                <div class="flex flex-col gap-0.5">
                    <p class="text-xs font-medium uppercase tracking-wide text-purple-600 dark:text-purple-400 mb-3">
                        Step 2 of 2
                    </p>
                    <h3 class="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                        Chatbot Setup - Description
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        Optional — helps to create the inital system prompt for agent with AI.
                    </p>
                </div>

                <div class="flex flex-col gap-2 flex-1">
                    <label for="botDescription" class="text-sm font-medium text-gray-700 dark:text-gray-100">
                        Description <span class="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                    </label>
                    <div class="relative w-full flex-1 min-h-[8rem]">
                        <textarea id="botDescription" rows="6" maxlength="1000"
                            placeholder="How the chatbot behaves, its capabilities, tone, and scope..."
                            [value]="agentDescription()" (input)="onAgentDescriptionInput($event)"
                            class="w-full h-full min-h-[8rem] rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 pr-14 pb-8 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow resize-y"></textarea>
                        <span
                            class="absolute right-3 bottom-2.5 text-[11px] text-gray-600 dark:text-gray-400 font-semibold">
                            {{ agentDescription().length }} / 1000
                        </span>
                    </div>
                </div>

                <div class="flex items-center justify-between pt-2">
                    <div class="flex items-center gap-1 border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-lg cursor-pointer"
                        (click)="onSetupWizardBack()">
                        <app-icon-v2 name="arrowLeft" [size]="14" class="text-gray-600 dark:text-gray-400" />
                        <span class="text-gray-600 dark:text-gray-400 font-semibold">Back</span>
                    </div>
                    <app-button variant="primary" className="flex items-center justify-center gap-2 text-sm font-medium"
                        [disabled]="isCreatingAgent()" (click)="createAgent()">
                        {{ isCreatingAgent() ? 'Creating…' : 'Create agent' }}
                    </app-button>
                </div>
            </div>
        </div>
        </div>
    </div>
</div>
} @else {
<div [ngClass]="step() === 'workflow'
        ? 'flex flex-col gap-3 min-h-0 flex-1 w-full h-[calc(100vh-8rem)] max-h-[calc(100vh-8rem)] overflow-hidden'
        : 'space-y-6 backdrop-blur-sm'">
    <!-- Back — sits above the canvas header (bot + tabs) -->
    <div class="flex items-center shrink-0">
        <button type="button"
            class="group inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white/80 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm backdrop-blur-sm transition-colors duration-200 hover:border-purple-300 hover:bg-purple-50/80 hover:text-purple-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/40 focus-visible:ring-offset-2 dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-200 dark:hover:border-line-strong dark:hover:bg-white/10 dark:hover:text-white dark:focus-visible:ring-offset-gray-950"
            (click)="goToChatbotList()" aria-label="Back to chatbot list">
            <app-icon-v2 name="arrowLeft" [size]="17"
                class="text-gray-500 transition-colors group-hover:text-purple-600 dark:text-gray-400 dark:group-hover:text-white"></app-icon-v2>
            <span>Go Back</span>
        </button>
    </div>

    <!-- Canvas header: bot + breadcrumb | step tabs -->
    <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between shrink-0">
        <div class="flex items-center gap-3 min-w-[320px]">
            <div
                class="flex shrink-0 items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/25">
                <app-icon-v2 name="bot-message-square" [size]="20" class="text-white"></app-icon-v2>
            </div>
            <div class="flex flex-col min-w-0">
                <div class="flex items-center gap-1">
                    <span class="text-md font-medium text-gray-500 dark:text-gray-400">{{ headerProductLabel() ||
                        'product_name' }}</span>
                    <app-icon-v2 name="chevronRight" [size]="14" class="text-gray-400 dark:text-gray-500"></app-icon-v2>
                    <span class="text-md font-medium text-gray-900 dark:text-white">{{ agentName() }}</span>
                </div>
                <span class="text-xs text-gray-400 dark:text-gray-500">
                    Created {{ agentCreationDate() | globalDateFormat:'relative' }}
                </span>
            </div>
        </div>

        <div class="flex-1 flex min-w-0 justify-end group/tabs">
            <div class="relative w-fit max-w-full overflow-hidden">
                <!-- Navigation Chevrons -->
                @if (canScrollLeft() && step() !== 'agent') {
                <button type="button" (click)="scrollTabs('left')" 
                    class="nav-chevron absolute left-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-full shadow-md border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-white transition-all duration-300">
                    <app-icon-v2 name="chevronLeft" [size]="14" />
                </button>
                }

                @if (canScrollRight()) {
                <button type="button" (click)="scrollTabs('right')" 
                    class="nav-chevron absolute right-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-full shadow-md border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-white transition-all duration-300">
                    <app-icon-v2 name="chevronRight" [size]="14" />
                </button>
                }

            <div class="flex items-center gap-1 p-1.5 tabs-container" 
                 #tabsContainer 
                 (scroll)="updateScrollState()"
                 [class.mask-left]="canScrollLeft()"
                 [class.mask-right]="canScrollRight()">
                @for (tab of tabs; track tab.id) {
                <button #tabButton type="button" [class]="getTabClasses(tab)" (click)="setStep(tab.id)"
                    [attr.aria-selected]="isActiveTab(tab.id)" role="tab">
                    <div class="relative flex items-center gap-2.5 z-10">
                        <div class="relative">
                            <div class="flex items-center justify-center w-7 h-7 rounded-lg "
                                [class]="isActiveTab(tab.id)
                                ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-md shadow-purple-500/30'
                                : 'bg-gray-200 dark:bg-gray-700 group-hover:bg-gray-300 dark:group-hover:bg-gray-600 transition-colors duration-200'">
                                <app-icon-v2 [name]="tab.icon" [size]="14"
                                    [class]="isActiveTab(tab.id) ? 'text-white' : 'text-gray-500 dark:text-gray-400'"></app-icon-v2>
                            </div>
                        </div>
                        <span class="font-medium whitespace-nowrap">{{ tab.label }}</span>
                    </div>
                </button>
                }
            </div>

                <!-- Progress indicator line inside the w-fit container -->
                <div class="absolute -bottom-0 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div class="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                        [style.width.%]="((currentTabIndex() + 1) / tabs.length) * 100">
                    </div>
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <!-- <div class="flex items-center gap-2">
            <button type="button"
                class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 shadow-sm">
                <app-icon-v2 name="save" [size]="16"></app-icon-v2>
                <span>Save Draft</span>
            </button>
        </div> -->
    </div>



    <div
        [ngClass]="step() === 'workflow'
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
            : 'min-h-[200px] overflow-visible rounded-xl border border-gray-200/80 dark:border-gray-700/50 bg-gradient-to-br from-white via-gray-50/80 to-purple-50/40 dark:from-gray-950/80 dark:via-gray-950/60 dark:to-indigo-800/30 backdrop-blur-md p-6 shadow-md shadow-gray-200/60 dark:shadow-black/40 ring-1 ring-inset ring-purple-500/[0.07] dark:ring-purple-400/10'">
        @switch (step()) {
        @case ('agent') {
        <app-agent [botId]="botId()" />
        }
        @case ('workflow') {
        <app-workflow class="flex-1 min-h-0 flex flex-col overflow-hidden" [botId]="botId()" [botName]="agentName()" [chatbotKnowledgeBaseId]="knowledgeBaseId()" />
        }
        @case ('knowledge-base') {
        <app-knowledge-base [botId]="botId()" [botName]="agentName()" (documentUploaded)="handleKnowledgeBaseUpload($event)" />
        }
        @case ('tools') {
        <app-tools [botId]="botId()" (createToolClicked)="openToolsDrawer()" (editToolClicked)="openEditToolsDrawer($event)" />
        }
        @case ('analysis') {
        <app-analysis [botId]="botId()" />
        }
        @case ('security') {
        <app-security [botId]="botId()" />
        }
        @case ('config') {
        <app-config [botId]="botId()" />
        }
        @case ('schedule') {
        <app-schedule [botId]="botId()" />
        }
        }
    </div>

    <!-- Floating Chatbot Preview is created programmatically in the component -->
</div>
}

@if (step() !== 'setup') {
<!-- ══ Tools Drawer (fixed at viewport level, escaping backdrop-blur stacking context) ══ -->

<!-- Backdrop -->
<div class="fixed bg-black/20 backdrop-blur-[2px] transition-[opacity,visibility] duration-200 ease-out dark:bg-black/50 top-16 left-0 right-0 bottom-0 z-[100]"
    [class.opacity-0]="!toolDrawerOpen()"
    [class.invisible]="!toolDrawerOpen()"
    [class.pointer-events-none]="!toolDrawerOpen()"
    [class.opacity-100]="toolDrawerOpen()"
    [class.visible]="toolDrawerOpen()"
    (click)="closeToolsDrawer()" aria-hidden="true">
</div>

<!-- Slide-over -->
<aside class="fixed flex w-[520px] flex-col overflow-hidden border-l bg-white/95 backdrop-blur-md transition-transform duration-[380ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform dark:bg-gray-950/95 top-16 right-0 bottom-0 z-[1000]"
    [ngClass]="toolDrawerOpen()
        ? 'border-gray-200/90 dark:border-gray-700/90 shadow-xl shadow-gray-900/10 dark:shadow-2xl dark:shadow-black/50'
        : 'border-transparent shadow-none'"
    [class.translate-x-full]="!toolDrawerOpen()"
    [class.translate-x-0]="toolDrawerOpen()"
    [class.pointer-events-none]="!toolDrawerOpen()"
    [class.pointer-events-auto]="toolDrawerOpen()"
    [attr.aria-hidden]="toolDrawerOpen() ? null : 'true'"
    [attr.inert]="toolDrawerOpen() ? null : ''"
    (click)="$event.stopPropagation()">

    <!-- Header -->
    <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200/80 bg-white/90 dark:border-gray-800/80 dark:bg-gray-900/80 backdrop-blur-sm shrink-0">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <app-icon-v2 name="Settings" [size]="16" />
            {{ editingToolId() ? 'Edit Tool' : 'Create Tool' }}
        </h3>
        <button type="button"
            class="inline-flex items-center justify-center p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400"
            (click)="closeToolsDrawer()" aria-label="Close panel">
            <app-icon-v2 name="X" [size]="18" />
        </button>
    </div>

    <!-- DATABASE / API tab row -->
    <div class="flex gap-0.5 overflow-x-auto px-2 pt-2 pb-0 border-b border-gray-200/80 dark:border-gray-800/80 shrink-0 bg-white/50 dark:bg-gray-900/40">
        <button type="button"
            class="shrink-0 px-3 py-2 text-xs font-semibold rounded-t-lg border transition-colors"
            [ngClass]="isToolDatabase()
                ? 'bg-white dark:bg-gray-950 border-gray-200/80 dark:border-gray-800/80 border-b-white dark:border-b-gray-950 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'"
            (click)="setToolType('DATABASE')">
            Database
        </button>
        <button type="button"
            class="shrink-0 px-3 py-2 text-xs font-semibold rounded-t-lg border transition-colors"
            [ngClass]="!isToolDatabase()
                ? 'bg-white dark:bg-gray-950 border-gray-200/80 dark:border-gray-800/80 border-b-white dark:border-b-gray-950 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'"
            (click)="setToolType('API')">
            API
        </button>
    </div>

    <!-- Scrollable form body -->
    <div class="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-5">

        <!-- Name -->
        <div class="flex flex-col gap-1.5">
            <label class="text-sm font-semibold text-gray-900 dark:text-white">Name <span class="text-red-500">*</span></label>
            <input type="text" [ngModel]="toolForm().name" (ngModelChange)="patchToolForm({ name: $event })"
                placeholder="e.g. Get Customer By ID"
                class="w-full rounded-lg border px-3 py-2.5 text-sm bg-white dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 transition-colors"
                [ngClass]="toolFormErrors()['name'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'" />
            @if (toolFormErrors()['name']) {
            <p class="text-xs text-red-500">{{ toolFormErrors()['name'] }}</p>
            }
        </div>

        <!-- Description -->
        <div class="flex flex-col gap-1.5">
            <label class="text-sm font-semibold text-gray-900 dark:text-white">Description <span class="text-red-500">*</span></label>
            <p class="text-xs text-gray-500 dark:text-gray-400">The AI uses this to decide when to call this tool.</p>
            <textarea rows="3" [ngModel]="toolForm().description" (ngModelChange)="patchToolForm({ description: $event })"
                placeholder="What does this tool do?"
                class="w-full rounded-lg border px-3 py-2.5 text-sm bg-white dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y min-h-[80px] transition-colors"
                [ngClass]="toolFormErrors()['description'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'"></textarea>
            @if (toolFormErrors()['description']) {
            <p class="text-xs text-red-500">{{ toolFormErrors()['description'] }}</p>
            }
        </div>

        <!-- Guardrails -->
        <div class="flex flex-col gap-1.5">
            <label class="text-sm font-semibold text-gray-900 dark:text-white">Guardrails</label>
            <p class="text-xs text-gray-500 dark:text-gray-400">Optional constraints the AI must respect when using this tool.</p>
            <textarea rows="2" [ngModel]="toolForm().guardrails" (ngModelChange)="patchToolForm({ guardrails: $event })"
                placeholder="e.g. customer_id must be a valid positive integer."
                class="w-full rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-sm bg-white dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y min-h-[60px]"></textarea>
        </div>

        <!-- Global toggle -->
        <div class="flex items-center justify-between">
            <div>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">Global tool</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Available across all chatbots in this workspace.</p>
            </div>
            <button type="button" role="switch" [attr.aria-checked]="toolForm().isGlobal"
                class="relative inline-flex h-6 w-10 items-center rounded-full border-2 transition-colors duration-200"
                [ngClass]="toolForm().isGlobal ? 'bg-violet-600 border-violet-600' : 'bg-gray-200 dark:bg-gray-700 border-gray-200 dark:border-gray-700'"
                (click)="patchToolForm({ isGlobal: !toolForm().isGlobal })">
                <span class="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
                    [ngClass]="toolForm().isGlobal ? 'translate-x-4' : 'translate-x-0.5'"></span>
            </button>
        </div>

        <div class="h-px bg-gray-100 dark:bg-gray-800"></div>

        <!-- ── DATABASE ── -->
        @if (isToolDatabase()) {
        <div class="flex flex-col gap-1.5">
            <label class="text-sm font-semibold text-gray-900 dark:text-white">MySQL Query <span class="text-red-500">*</span></label>
            <p class="text-xs text-gray-500 dark:text-gray-400">
                Use <code class="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded text-violet-600 dark:text-violet-400">{{ toolPh.varSyntax }}</code> for dynamic parameters.
            </p>
            <textarea rows="8" [ngModel]="toolForm().mysqlQuery" (ngModelChange)="patchToolForm({ mysqlQuery: $event })"
                [placeholder]="toolPh.dbQuery"
                class="w-full rounded-lg border px-3 py-2.5 text-sm font-mono bg-gray-50 dark:bg-gray-900/60 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y min-h-[160px] transition-colors"
                [ngClass]="toolFormErrors()['mysqlQuery'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'"></textarea>
            @if (toolFormErrors()['mysqlQuery']) {
            <p class="text-xs text-red-500">{{ toolFormErrors()['mysqlQuery'] }}</p>
            }
        </div>
        }

        <!-- ── API ── -->
        @if (!isToolDatabase()) {
        <div class="flex flex-col gap-5">

            <!-- URL -->
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-semibold text-gray-900 dark:text-white">API URL <span class="text-red-500">*</span></label>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    Supports <code class="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded text-violet-600 dark:text-violet-400">{{ toolPh.varSyntax }}</code> path placeholders.
                </p>
                <input type="text" [ngModel]="toolForm().apiUrl" (ngModelChange)="patchToolForm({ apiUrl: $event })"
                    [placeholder]="toolPh.apiUrl"
                    class="w-full rounded-lg border px-3 py-2.5 text-sm font-mono bg-white dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 transition-colors"
                    [ngClass]="toolFormErrors()['apiUrl'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'" />
                @if (toolFormErrors()['apiUrl']) {
                <p class="text-xs text-red-500">{{ toolFormErrors()['apiUrl'] }}</p>
                }
            </div>

            <!-- Method -->
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-semibold text-gray-900 dark:text-white">Method</label>
                <div class="flex gap-2 flex-wrap">
                    @for (m of toolApiMethodOptions; track m) {
                    <button type="button"
                        class="px-3.5 py-1.5 rounded-lg text-xs font-bold border transition-colors"
                        [ngClass]="toolForm().apiMethod === m
                            ? 'bg-violet-600 border-violet-600 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'"
                        (click)="patchToolForm({ apiMethod: m })">{{ m }}</button>
                    }
                </div>
            </div>

            <!-- Headers -->
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-semibold text-gray-900 dark:text-white">Headers <span class="text-xs text-gray-400 font-normal">(JSON)</span></label>
                <textarea rows="3" [ngModel]="toolForm().apiHeaders" (ngModelChange)="patchToolForm({ apiHeaders: $event })"
                    placeholder='{"Content-Type": "application/json"}'
                    class="w-full rounded-lg border px-3 py-2.5 text-xs font-mono bg-gray-50 dark:bg-gray-900/60 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y min-h-[72px] transition-colors"
                    [ngClass]="toolFormErrors()['apiHeaders'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'"></textarea>
                @if (toolFormErrors()['apiHeaders']) {
                <p class="text-xs text-red-500">{{ toolFormErrors()['apiHeaders'] }}</p>
                }
            </div>

            <!-- Body -->
            <div class="flex flex-col gap-1.5">
                <label class="text-sm font-semibold text-gray-900 dark:text-white">Body <span class="text-xs text-gray-400 font-normal">(JSON)</span></label>
                <textarea rows="3" [ngModel]="toolForm().apiBody" (ngModelChange)="patchToolForm({ apiBody: $event })"
                    placeholder='{"key": "value"}'
                    class="w-full rounded-lg border px-3 py-2.5 text-xs font-mono bg-gray-50 dark:bg-gray-900/60 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y min-h-[72px] transition-colors"
                    [ngClass]="toolFormErrors()['apiBody'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'"></textarea>
                @if (toolFormErrors()['apiBody']) {
                <p class="text-xs text-red-500">{{ toolFormErrors()['apiBody'] }}</p>
                }
            </div>

            <!-- Params + Query -->
            <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-semibold text-gray-900 dark:text-white">Params <span class="text-xs text-gray-400 font-normal">(JSON)</span></label>
                    <textarea rows="3" [ngModel]="toolForm().apiParams" (ngModelChange)="patchToolForm({ apiParams: $event })"
                        [placeholder]="toolPh.apiParams"
                        class="w-full rounded-lg border px-3 py-2 text-xs font-mono bg-gray-50 dark:bg-gray-900/60 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y min-h-[72px] transition-colors"
                        [ngClass]="toolFormErrors()['apiParams'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'"></textarea>
                    @if (toolFormErrors()['apiParams']) {
                    <p class="text-xs text-red-500">{{ toolFormErrors()['apiParams'] }}</p>
                    }
                </div>
                <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-semibold text-gray-900 dark:text-white">Query <span class="text-xs text-gray-400 font-normal">(JSON)</span></label>
                    <textarea rows="3" [ngModel]="toolForm().apiQuery" (ngModelChange)="patchToolForm({ apiQuery: $event })"
                        [placeholder]="toolPh.apiQuery"
                        class="w-full rounded-lg border px-3 py-2 text-xs font-mono bg-gray-50 dark:bg-gray-900/60 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y min-h-[72px] transition-colors"
                        [ngClass]="toolFormErrors()['apiQuery'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'"></textarea>
                    @if (toolFormErrors()['apiQuery']) {
                    <p class="text-xs text-red-500">{{ toolFormErrors()['apiQuery'] }}</p>
                    }
                </div>
            </div>

            <!-- Auth -->
            <div class="flex flex-col gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50/50 dark:bg-gray-900/30">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">Authentication <span class="text-xs font-normal text-gray-400">(optional)</span></p>
                <div class="flex gap-2 flex-wrap">
                    @for (opt of toolAuthTypeOptions; track opt.value) {
                    <button type="button"
                        class="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
                        [ngClass]="toolForm().apiAuthType === opt.value
                            ? 'bg-violet-600 border-violet-600 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'"
                        (click)="patchToolForm({ apiAuthType: toolForm().apiAuthType === opt.value ? '' : opt.value })">{{ opt.label }}</button>
                    }
                </div>
                @if (toolForm().apiAuthType) {
                <div class="grid grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label class="text-xs font-medium text-gray-500 dark:text-gray-400">Token / Secret</label>
                        <input type="text" [ngModel]="toolForm().apiAuthToken" (ngModelChange)="patchToolForm({ apiAuthToken: $event })"
                            placeholder="my-secret-token"
                            class="w-full rounded-lg border px-3 py-2 text-xs bg-white dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 transition-colors"
                            [ngClass]="toolFormErrors()['apiAuthToken'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'" />
                        @if (toolFormErrors()['apiAuthToken']) {
                        <p class="text-[10px] text-red-500 mt-0.5">{{ toolFormErrors()['apiAuthToken'] }}</p>
                        }
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-xs font-medium text-gray-500 dark:text-gray-400">Key name</label>
                        <input type="text" [ngModel]="toolForm().apiAuthKeyname" (ngModelChange)="patchToolForm({ apiAuthKeyname: $event })"
                            placeholder="Authorization"
                            class="w-full rounded-lg border px-3 py-2 text-xs bg-white dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 transition-colors"
                            [ngClass]="toolFormErrors()['apiAuthKeyname'] ? 'border-red-400 dark:border-red-600' : 'border-gray-200 dark:border-gray-700'" />
                        @if (toolFormErrors()['apiAuthKeyname']) {
                        <p class="text-[10px] text-red-500 mt-0.5">{{ toolFormErrors()['apiAuthKeyname'] }}</p>
                        }
                    </div>
                </div>
                }
            </div>
        </div>
        }

    </div>

    <!-- Footer -->
    <div class="shrink-0 px-6 py-4 border-t border-gray-200/80 dark:border-gray-800/80 flex items-center justify-between bg-white/90 dark:bg-gray-900/80">
        <button type="button"
            class="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer"
            (click)="closeToolsDrawer()" [disabled]="toolSaving()">Cancel</button>
        <button type="button"
            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white shadow-md shadow-violet-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            [disabled]="toolSaving()"
            (click)="submitTool()">
            @if (toolSaving()) {
            <span class="inline-block w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin"></span>
            Saving…
            } @else {
            {{ editingToolId() ? 'Update Tool' : 'Save Tool' }}
            }
        </button>
    </div>

</aside>
}

<!-- ══ Global Analysis Session Browser Drawer ══ -->
@if (whitelabelChatService.selectedSource(); as source) {
    <!-- Backdrop (Z-index 100 below drawer 1000) -->
    <div class="fixed bg-black/20 backdrop-blur-[2px] transition-[opacity,visibility] duration-200 ease-out dark:bg-black/50 top-16 left-0 right-0 bottom-0 z-[100]"
         [class.opacity-0]="!whitelabelChatService.isAnalysisDrawerOpen()"
         [class.pointer-events-none]="!whitelabelChatService.isAnalysisDrawerOpen()"
         [class.opacity-100]="whitelabelChatService.isAnalysisDrawerOpen()"
         (click)="whitelabelChatService.closeAnalysisDrawer()" aria-hidden="true">
    </div>

    <!-- Drawer (Matches Tools Drawer Z-index and width patterns) -->
    <aside class="fixed flex w-[1100px] flex-col overflow-hidden border-l bg-white/95 dark:bg-gray-950/95 backdrop-blur-md transition-transform duration-[380ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform top-16 right-0 bottom-0 z-[1000] border-gray-200/90 dark:border-gray-700/90 shadow-xl shadow-gray-900/10 dark:shadow-2xl dark:shadow-black/50"
           [class.translate-x-0]="whitelabelChatService.isAnalysisDrawerOpen()"
           [class.translate-x-full]="!whitelabelChatService.isAnalysisDrawerOpen()"
           (click)="$event.stopPropagation()">

        <!-- Header (Twin of Create Tool Header) -->
        <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200/80 bg-white/90 dark:border-gray-800/80 dark:bg-gray-900/80 backdrop-blur-sm shrink-0">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 text-transform capitalize">
                <app-icon-v2 [name]="'Settings'" [size]="16" />
                Source: {{ source.source || 'External' | titlecase }}
            </h3>
            <button type="button"
                class="inline-flex items-center justify-center p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400 transition-colors"
                (click)="whitelabelChatService.closeAnalysisDrawer()" aria-label="Close panel">
                <app-icon-v2 name="x" [size]="18" />
            </button>
        </div>

        <!-- Content Area -->
        <div class="flex-1 min-h-0 flex overflow-hidden">
            <!-- Left: Sessions List -->
            <div class="border-r border-gray-200/80 dark:border-gray-800/80 flex flex-col bg-white/50 dark:bg-gray-900/40 transition-all duration-300"
                 [class]="selectedTraceMessage() ? 'w-[80px]' : 'w-1/3'">
                <div class="px-4 py-2 border-b border-gray-200/80 dark:border-gray-800/80 shrink-0 bg-gray-50/50 dark:bg-gray-800/20 truncate">
                    <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest whitespace-nowrap" [title]="source.sessionCount + ' Total Sessions'">
                        {{ selectedTraceMessage() ? source.sessionCount : source.sessionCount + ' Total Sessions' }}
                    </span>
                </div>
                <div class="flex-1 overflow-y-auto p-2 space-y-1">
                    @for (session of source.sessionStats; track session.sessionId) {
                        <button (click)="whitelabelChatService.selectSession(session.sessionId)"
                                class="w-full text-left p-3 rounded-xl transition-all duration-200 group relative border"
                                [class]="whitelabelChatService.selectedSessionId() === session.sessionId
                                    ? 'bg-white dark:bg-gray-800 border-violet-200 dark:border-violet-900/50 shadow-sm ring-1 ring-violet-500/10'
                                    : 'bg-transparent border-transparent hover:bg-white/50 dark:hover:bg-gray-800/50 hover:border-gray-200 dark:hover:border-gray-800'">
                            
                            <div class="flex flex-col gap-1.5 text-xs">
                                <div class="flex items-center justify-between">
                                    <span class="font-bold font-mono text-gray-400 group-hover:text-violet-400 transition-colors truncate"
                                          [class]="selectedTraceMessage() ? 'text-[10px] max-w-[40px]' : 'max-w-[240px]'">
                                        #{{ session.sessionId.split('-').pop() }}
                                    </span>
                                    @if (whitelabelChatService.selectedSessionId() === session.sessionId) {
                                        <span class="w-1.5 h-1.5 shrink-0 rounded-full bg-violet-600 dark:bg-violet-400"></span>
                                    }
                                </div>
                                @if (!selectedTraceMessage()) {
                                    <div class="flex flex-col">
                                        <span class="font-bold text-gray-700 dark:text-gray-200 tabular-nums">{{ session.tokensUsed | number }} tokens</span>
                                        <span class="text-[10px] text-gray-500">{{ session.userMessageCount }} msgs · {{ session.aiResponseCount }} resp</span>
                                    </div>
                                }
                            </div>
                        </button>
                    }
                </div>
            </div>

            <!-- Middle/Right wrapper -->
            <div class="flex-1 flex overflow-hidden">
                <!-- Middle: Conversation Browser -->
                <div class="flex-1 flex flex-col bg-white dark:bg-gray-950 overflow-hidden transition-all duration-300"
                     [class]="selectedTraceMessage() ? 'border-r border-gray-200/80 dark:border-gray-800/80' : 'border-none'">
                @if (whitelabelChatService.isMessagesLoading()) {
                    <div class="flex-1 flex flex-col items-center justify-center gap-3 opacity-50">
                        <app-icon-v2 name="loaderCircle" [size]="24" class="animate-spin text-violet-500"></app-icon-v2>
                        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Loading conversation...</span>
                    </div>
                } @else if (sessionTimeline().length > 0) {
                    <div class="flex-1 overflow-y-auto p-6 space-y-6">
                        @for (item of sessionTimeline(); track item.key) {
                            @if (item.kind === 'documents') {
                                <div class="flex flex-col gap-2 items-start">
                                    <div class="flex items-center gap-2">
                                        <div class="w-6 h-6 rounded-lg flex items-center justify-center border bg-violet-50 dark:bg-violet-900/30 text-violet-500 border-violet-100 dark:border-violet-900/20">
                                            <app-icon-v2 name="file" [size]="12"></app-icon-v2>
                                        </div>
                                        <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                                            documents · {{ item.at | date:'h:mm a' }}
                                        </span>
                                    </div>
                                    <div class="max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm border bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 rounded-tl-none border-violet-100 dark:border-violet-900/30 shadow-violet-500/5">
                                        <div class="text-xs font-bold text-gray-900 dark:text-white">Documents uploaded</div>
                                        <div class="mt-2 flex flex-wrap gap-2">
                                            @for (doc of item.docs; track doc.id) {
                                                <a class="group inline-flex max-w-full items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-950/40 px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:border-violet-300 dark:hover:border-line-strong hover:bg-white dark:hover:bg-gray-900/40 transition-all"
                                                   [href]="doc.downloadUrl || doc.publicUrl"
                                                   target="_blank"
                                                   rel="noopener noreferrer"
                                                   [title]="doc.fileName">
                                                    <app-icon-v2 name="paperclip" [size]="14" class="text-gray-400 group-hover:text-violet-500 transition-colors"></app-icon-v2>
                                                    <span class="truncate max-w-[260px]">{{ doc.fileName }}</span>
                                                    <span class="text-[10px] font-mono text-gray-400 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-gray-200/70 dark:border-gray-800/70">
                                                        {{ (doc.size / 1024) | number:'1.0-0' }} KB
                                                    </span>
                                                </a>
                                            }
                                        </div>
                                    </div>
                                </div>
                            } @else {
                                <div class="flex flex-col gap-2 {{ item.message.role === 'user' ? 'items-end' : 'items-start' }}">
                                    <div class="flex items-center gap-2 {{ item.message.role === 'user' ? 'flex-row-reverse' : 'flex-row' }}">
                                        <div class="w-6 h-6 rounded-lg flex items-center justify-center border {{ item.message.role === 'user' ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 border-gray-200 dark:border-gray-700' : 'bg-violet-50 dark:bg-violet-900/30 text-violet-500 border-violet-100 dark:border-violet-900/20' }}">
                                            <app-icon-v2 [name]="item.message.role === 'user' ? 'user' : 'bot'" [size]="12"></app-icon-v2>
                                        </div>
                                        <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                                            {{ item.message.role }} · {{ item.at | date:'h:mm a' }}
                                        </span>
                                    </div>
                                    <div class="max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm border {{ item.message.role === 'user' 
                                            ? 'bg-gray-50 dark:bg-gray-900/50 text-gray-800 dark:text-gray-200 rounded-tr-none border-gray-200 dark:border-gray-800' 
                                            : 'bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 rounded-tl-none border-violet-100 dark:border-violet-900/30 shadow-violet-500/5' }}">
                                        <markdown [data]="item.message.content" class="markdown-content"></markdown>
                                        @if (item.message.role === 'assistant' && item.message.trace) {
                                          <div class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50 flex flex-col gap-3">
                                              <button (click)="toggleTrace(item.message.id)" 
                                                      class="w-fit flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all duration-200
                                                             {{ selectedTraceMessage()?.id === item.message.id 
                                                                ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-800/50' 
                                                                : 'bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700/50 hover:border-violet-300 dark:hover:border-line-strong' }}">
                                                  <app-icon-v2 [name]="selectedTraceMessage()?.id === item.message.id ? 'chevron-right' : 'info'" [size]="12"></app-icon-v2>
                                                  {{ selectedTraceMessage()?.id === item.message.id ? 'Close Trace' : 'View Trace' }}
                                              </button>
                                          </div>
                                        }
                                    </div>
                                </div>
                            }
                        }
                    </div>
                } @else if (whitelabelChatService.selectedSessionId()) {
                    <div class="flex-1 flex flex-col items-center justify-center p-12 text-center">
                        <div class="w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center mb-4 text-gray-300">
                            <app-icon-v2 name="message-circle-off" [size]="24"></app-icon-v2>
                        </div>
                        <h4 class="text-sm font-bold text-gray-900 dark:text-white">No messages found</h4>
                        <p class="text-xs text-gray-500 mt-1 max-w-[200px]">This session might have been initialized but no messages were exchanged.</p>
                    </div>
                } @else {
                    <div class="flex-1 flex flex-col items-center justify-center p-12 text-center opacity-40">
                        <app-icon-v2 name="mouse-pointer-click" [size]="32" class="mb-4 text-gray-300"></app-icon-v2>
                        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">Select a session on the left to view history</span>
                    </div>
                }
                </div> <!-- End Middle -->

                <!-- Right: Trace Panel -->
                @if (selectedTraceMessage(); as traceMsg) {
                    <div class="w-[450px] flex flex-col bg-gray-50/50 dark:bg-gray-900/20 overflow-hidden shadow-inner animate-in slide-in-from-right duration-300">
                        <div class="px-5 py-3 border-b border-gray-200/80 dark:border-gray-800/80 flex items-center justify-between bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm shrink-0">
                            <span class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                                <span class="w-6 h-6 rounded-md bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 flex items-center justify-center">
                                    <app-icon-v2 name="activity" [size]="14"></app-icon-v2>
                                </span>
                                Execution Trace
                            </span>
                            <button (click)="toggleTrace(traceMsg.id)" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                                <app-icon-v2 name="x" [size]="16"></app-icon-v2>
                            </button>
                        </div>
                        
                        <div class="flex-1 overflow-y-auto p-5 pb-10 flex flex-col gap-6">
                            <!-- Trace Stats -->
                            <div class="grid grid-cols-4 gap-3">
                                <div class="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm flex flex-col gap-1">
                                    <span class="text-[9px] font-bold text-gray-400 uppercase tracking-tight flex items-center gap-1">
                                        <app-icon-v2 name="clock" [size]="10"></app-icon-v2> Duration
                                    </span>
                                    <span class="text-sm font-mono font-bold text-gray-800 dark:text-gray-200">{{ traceMsg.trace?.durationMs }}ms</span>
                                </div>
                                <div class="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm flex flex-col gap-1">
                                    <span class="text-[9px] font-bold text-gray-400 uppercase tracking-tight flex items-center gap-1">
                                        <app-icon-v2 name="arrow-right-left" [size]="10"></app-icon-v2> Input
                                    </span>
                                    <span class="text-sm font-mono font-bold text-gray-800 dark:text-gray-200">{{ traceMsg.trace?.inputTokens }}</span>
                                </div>
                                <div class="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm flex flex-col gap-1">
                                    <span class="text-[9px] font-bold text-gray-400 uppercase tracking-tight flex items-center gap-1">
                                        <app-icon-v2 name="arrow-right" [size]="10"></app-icon-v2> Output
                                    </span>
                                    <span class="text-sm font-mono font-bold text-gray-800 dark:text-gray-200">{{ traceMsg.trace?.outputTokens }}</span>
                                </div>
                                <div class="bg-violet-50 dark:bg-violet-900/10 p-3 rounded-xl border border-violet-100 dark:border-violet-900/30 shadow-sm flex flex-col gap-1">
                                    <span class="text-[9px] font-bold text-violet-400 uppercase tracking-tight flex items-center gap-1">
                                        <app-icon-v2 name="hash" [size]="10"></app-icon-v2> Total
                                    </span>
                                    <span class="text-sm font-mono font-bold text-violet-600 dark:text-violet-400">{{ traceMsg.trace?.totalTokens }}</span>
                                </div>
                            </div>

                            <!-- Trace Nodes Timeline -->
                            <div class="flex flex-col gap-3">
                                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">Execution Chain</span>
                                <div class="flex flex-col gap-2 pl-3 border-l-2 border-violet-200 dark:border-violet-800/40">
                                    @for (node of traceMsg.trace?.nodes; track node.executionNodePid) {
                                        <div class="relative flex flex-col gap-1 pl-4 pb-4 last:pb-2">
                                            <!-- Timeline Dot -->
                                            <div class="absolute -left-[11px] top-1.5 w-5 h-5 rounded-full border-[6px] border-gray-50 dark:border-gray-900 {{ node.isError ? 'bg-red-500' : 'bg-violet-500' }} shadow-sm"></div>
                                            
                                            <div class="flex flex-col gap-0.5 mt-0.5">
                                              <div class="flex items-center justify-between gap-2">
                                                <span class="text-sm font-bold text-gray-800 dark:text-gray-200 truncate" [title]="node.nodeName">{{ node.nodeName }}</span>
                                                <span class="text-[10px] font-mono text-gray-400 bg-white dark:bg-gray-900 px-1.5 py-0.5 rounded border border-gray-200/80 dark:border-gray-800 shrink-0">{{ node.durationMs }}ms</span>
                                              </div>
                                              @if (node.calledByNodeName) {
                                                <span class="text-[10px] text-gray-400 italic flex items-center gap-1">
                                                    <app-icon-v2 name="corner-down-right" [size]="10" class="opacity-70"></app-icon-v2>
                                                    called by {{ node.calledByNodeName }}
                                                </span>
                                              }
                                            </div>
                                            
                                            <div class="flex flex-wrap gap-2 mt-1.5">
                                              <div class="flex items-center gap-1.5 text-[10px] text-gray-500 bg-white dark:bg-gray-900 px-2 py-1 rounded-md border border-gray-200/80 dark:border-gray-800 shadow-sm">
                                                <app-icon-v2 name="arrow-right-left" [size]="10" class="opacity-50"></app-icon-v2>
                                                <span class="uppercase font-medium tracking-tight">{{ node.inputTokens }} in</span>
                                              </div>
                                              <div class="flex items-center gap-1.5 text-[10px] text-gray-500 bg-white dark:bg-gray-900 px-2 py-1 rounded-md border border-gray-200/80 dark:border-gray-800 shadow-sm">
                                                <app-icon-v2 name="arrow-right" [size]="10" class="opacity-50"></app-icon-v2>
                                                <span class="uppercase font-medium tracking-tight">{{ node.outputTokens }} out</span>
                                              </div>
                                            </div>

                                            @if (node.tools.length > 0) {
                                              <div class="mt-3 flex flex-col gap-2">
                                                @for (tool of node.tools; track tool.toolName) {
                                                  <div class="flex flex-col gap-1.5 bg-white dark:bg-gray-900/50 p-2 rounded-xl border border-amber-100 dark:border-amber-900/20 shadow-sm">
                                                    <button (click)="toggleTool(traceMsg.id + '-' + node.executionNodePid + '-' + tool.toolName)"
                                                            class="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-lg text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-100/50 dark:border-amber-900/30 text-amber-700 dark:text-amber-400 hover:border-amber-300 dark:hover:border-amber-700/50 transition-all">
                                                      <div class="flex items-center gap-2">
                                                        <app-icon-v2 name="wrench" [size]="12" class="opacity-80"></app-icon-v2>
                                                        <span class="font-bold">{{ tool.toolName }}</span>
                                                        <span class="opacity-80 px-1.5 py-0.5 text-[10px] bg-amber-100 dark:bg-amber-900/50 rounded-md font-mono">{{ tool.callCount }} runs</span>
                                                      </div>
                                                      <app-icon-v2 [name]="expandedToolKeys().has(traceMsg.id + '-' + node.executionNodePid + '-' + tool.toolName) ? 'chevron-up' : 'chevron-down'" [size]="14" class="opacity-60"></app-icon-v2>
                                                    </button>

                                                    @if (expandedToolKeys().has(traceMsg.id + '-' + node.executionNodePid + '-' + tool.toolName)) {
                                                      <div class="flex flex-col gap-3 pt-2 pb-1 animate-in slide-in-from-top-1 fade-in duration-200">
                                                        @for (call of tool.calls; track call.stepIndex) {
                                                          <div class="flex flex-col gap-2 bg-gray-50 dark:bg-gray-900/80 p-2.5 rounded-lg border border-gray-200/80 dark:border-gray-800">
                                                            <div class="flex items-center justify-between pb-1 border-b border-gray-200/50 dark:border-gray-800/50">
                                                              <span class="text-[10px] font-bold text-gray-500">CALL #{{ call.stepIndex + 1 }}</span>
                                                              @if (call.durationMs) {
                                                                <span class="text-[10px] font-mono text-gray-400 bg-white dark:bg-gray-950 px-1.5 py-0.5 rounded shadow-sm">{{ call.durationMs }}ms</span>
                                                              }
                                                            </div>
                                                            
                                                            @if (call.input) {
                                                              <div class="flex flex-col gap-1">
                                                                <span class="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1"><app-icon-v2 name="arrow-right-left" [size]="10"></app-icon-v2> Input</span>
                                                                <pre class="text-[10px] font-mono p-2 bg-white dark:bg-gray-950 rounded-md border border-gray-200/80 dark:border-gray-800 overflow-x-auto text-gray-700 dark:text-gray-300 shadow-inner">{{ call.input | json }}</pre>
                                                              </div>
                                                            }

                                                            @if (call.output) {
                                                              <div class="flex flex-col gap-1 mt-1">
                                                                <span class="text-[9px] font-bold text-violet-400 uppercase tracking-wider flex items-center gap-1"><app-icon-v2 name="arrow-right" [size]="10"></app-icon-v2> Output</span>
                                                                <pre class="text-[10px] font-mono p-2 bg-violet-50/50 dark:bg-violet-900/20 rounded-md border border-violet-100 dark:border-violet-900/30 overflow-x-auto text-violet-700 dark:text-violet-300 shadow-inner max-h-[250px]">{{ call.output | json }}</pre>
                                                              </div>
                                                            }

                                                            @if (call.error) {
                                                              <div class="flex flex-col gap-1 mt-1">
                                                                <span class="text-[9px] font-bold text-red-500 uppercase tracking-wider flex items-center gap-1"><app-icon-v2 name="alert-triangle" [size]="10"></app-icon-v2> Error</span>
                                                                <p class="text-[10px] text-red-600 dark:text-red-400 italic bg-red-50 dark:bg-red-900/10 p-2 rounded-md border border-red-100 dark:border-red-900/20">{{ call.error }}</p>
                                                              </div>
                                                            }
                                                          </div>
                                                        }
                                                      </div>
                                                    }
                                                  </div>
                                                }
                                              </div>
                                            }
                                            
                                            @if (node.isError) {
                                              <div class="mt-2 p-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-xs text-red-600 dark:text-red-400 italic flex items-center gap-2">
                                                <app-icon-v2 name="alert-circle" [size]="14"></app-icon-v2>
                                                {{ node.errorMessage || 'Unknown execution error' }}
                                              </div>
                                            }
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div> <!-- End Middle/Right wrapper -->
        </div>
    </aside>
}

